/**
 * The client-TOLERANT half of R9 §3.1 — the reader `packages/core`'s server-strict parser
 * deliberately is not.
 *
 * `parseClientConfigDocument` (core) refuses a document on any malformation, because a refused
 * parameter must page rather than serve (R9 §6). This module is the other posture, for the other
 * side of the wire: a document that reaches a device has already passed the server, so what this
 * reader sees is **version skew**, not operator error — an older binary meeting a newer document
 * (a flag name it does not know) or a newer binary meeting an older one (a flag the document does
 * not name). Both are ordinary, and both resolve without a refusal:
 *
 *  - a flag name the registry does not carry is **ignored**;
 *  - a registered flag the document omits, or carries with an unusable value, takes the
 *    **registry default**;
 *  - a rule object resolves through R8's cohort evaluator when this client has an account
 *    identity to hash, and to its **`off` arm** when it does not (D321/D325 — R8's S3 replaced
 *    exactly that one branch and nothing else here);
 *  - `min_build_number`, `prompt_build_number` (D345) and `notice` are kept when well-formed and
 *    dropped when not — a malformed soft floor reads as *no soft floor*, which prompts nobody.
 *
 * The one thing that IS refused is a document with no integer `config_version` (R9 §3.1: the
 * client accepts "any document that parses and carries an integer `config_version`"), because
 * D328's *a lower version does not overwrite a higher* rule (bounded by v7.32's stale window) cannot be applied to a document that
 * has no version to compare.
 */

import {
  CLIENT_CONFIG_DEFAULTS,
  FLAG_REGISTRY,
  cohortBucket,
  isFlagRuleObject,
  resolveFlagArm,
  type ClientConfigDocument,
  type ClientConfigNotice,
  type FlagName,
  type FlagValue,
} from '@selogate/core'

function isPlainObject(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isNonNegativeInteger(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0
}

const FLAG_NAMES: ReadonlySet<string> = new Set(FLAG_REGISTRY.map((entry) => entry.name))

function isFlagName(candidate: string): candidate is FlagName {
  return FLAG_NAMES.has(candidate)
}

function readFlags(raw: unknown): Readonly<Partial<Record<FlagName, FlagValue>>> {
  if (!isPlainObject(raw)) return {}
  const flags: Partial<Record<FlagName, FlagValue>> = {}
  for (const [name, value] of Object.entries(raw)) {
    if (!isFlagName(name)) continue
    if (typeof value === 'boolean' || isFlagRuleObject(value)) flags[name] = value
  }
  return flags
}

function readNotice(raw: unknown): ClientConfigNotice | undefined {
  if (!isPlainObject(raw)) return undefined
  const id = raw['id']
  const text = raw['text']
  if (typeof id !== 'string' || id.length === 0 || !isPlainObject(text)) return undefined
  const entries = Object.entries(text).filter(
    (entry): entry is [string, string] => typeof entry[1] === 'string' && entry[1].length > 0,
  )
  if (entries.length === 0) return undefined
  return { id, text: Object.fromEntries(entries) }
}

/**
 * Reads a fetched or cached body as a document, keeping only what this build understands.
 * `undefined` means *not a document* — no integer `config_version` — and the caller keeps what it
 * has. Everything else is normalized rather than refused.
 */
export function readClientConfigDocument(raw: unknown): ClientConfigDocument | undefined {
  if (!isPlainObject(raw)) return undefined
  const config_version = raw['config_version']
  if (!isNonNegativeInteger(config_version)) return undefined
  const min_build_number = raw['min_build_number']
  const prompt_build_number = raw['prompt_build_number']
  const notice = readNotice(raw['notice'])
  return {
    config_version,
    ...(isNonNegativeInteger(min_build_number) && min_build_number > 0 ? { min_build_number } : {}),
    ...(isNonNegativeInteger(prompt_build_number) && prompt_build_number > 0 ? { prompt_build_number } : {}),
    flags: readFlags(raw['flags']),
    ...(notice === undefined ? {} : { notice }),
  }
}

/** What the rest of the client reads: every registered flag resolved to a boolean, once. */
export interface ResolvedClientConfig {
  readonly configVersion: number
  /** `undefined` is *no floor* — R9 §4.4; absent and `0` on the wire both land here. */
  readonly minBuildNumber: number | undefined
  /** D345's soft floor. `undefined` is *no soft floor*; absent and `0` on the wire both land here. */
  readonly promptBuildNumber: number | undefined
  readonly flags: Readonly<Record<FlagName, boolean>>
  readonly notice: ClientConfigNotice | undefined
}

/**
 * R9 §4.2 — resolved once per document load and memoized by the store; a flag is never re-read
 * under an in-flight action, so a document arriving mid-command lands at the next boundary.
 *
 * **`cohortIdentity` is the signed-in account's identity, or `undefined` for a client that has
 * none (D321, D325).** It is a required parameter rather than an optional one deliberately:
 * every call site has to say which of the two it means, and *forgetting* it would silently be
 * the signed-out answer — `off` for every cohorted flag, on every device, with nothing red.
 *
 * **The bucket is computed here, once per document load per flag, and nowhere else** (R8 §3.2's
 * *once per config load and memoized, not per call site*). `flag()` downstream is a property
 * lookup on the map this returns, so a bucket cannot be recomputed — and a bucket that is
 * recomputed can be recomputed differently, which is the stability claim S3's done-when makes.
 *
 * **The salt comes from the rule and the identity from the caller** — the two halves of
 * `cohortBucket`'s contract — so a per-flag salt gives independent draws (D323) with no state
 * held here.
 */
export function resolveClientConfig(
  document: ClientConfigDocument,
  cohortIdentity: string | undefined,
): ResolvedClientConfig {
  const flags = {} as Record<FlagName, boolean>
  for (const entry of FLAG_REGISTRY) {
    const value = document.flags[entry.name]
    if (typeof value === 'boolean') {
      flags[entry.name] = value
    } else if (value === undefined) {
      flags[entry.name] = entry.default
    } else if (cohortIdentity === undefined) {
      // **D325 — a client with no session is in no cohort and takes the `off` arm.** Not a
      // random id, not a device id, not a hash of nothing: each re-rolls on reinstall, which is
      // the stability failure D321 refused, and a coin flip that changes on reload makes the
      // flag unstable in exactly the window where a resident is least able to explain it.
      flags[entry.name] = value.off
    } else {
      // `resolveFlagArm` returns the arm NAME and never reads `on`/`off` — the document's shape
      // stays out of the bucket module (`cohort.ts`'s own docblock), and the substitution is
      // this reader's. A malformed or out-of-range rule fails closed to `off` there (D323).
      const arm = resolveFlagArm(value, cohortBucket(value.salt, cohortIdentity))
      flags[entry.name] = arm === 'on' ? value.on : value.off
    }
  }
  return {
    configVersion: document.config_version,
    minBuildNumber: document.min_build_number === undefined ? undefined : document.min_build_number,
    promptBuildNumber: document.prompt_build_number === undefined ? undefined : document.prompt_build_number,
    flags,
    notice: document.notice,
  }
}

/**
 * R9 §3.2's third source — the registry's own defaults, no floor, no notice, version `0`.
 *
 * Resolved with **no** identity, which costs nothing and is not a claim about the signed-in
 * state: {@link CLIENT_CONFIG_DEFAULTS} carries a plain boolean for every registered flag and no
 * rule object, so there is no arm for an identity to select.
 */
export const DEFAULT_CLIENT_CONFIG: ResolvedClientConfig = resolveClientConfig(
  CLIENT_CONFIG_DEFAULTS,
  undefined,
)
